import {
  Bell,
  CalendarDays,
  Heart,
  Home,
  MessageSquare,
  BarChart3,
  Building2,
  CreditCard,
  Handshake,
  RotateCcw,
  ListOrdered,
  Settings,
  Users,
  type LucideIcon,
} from 'lucide-react'

import type { Capabilities } from '@/api/types'
import { hasAllPermissions, type Permission } from '@/auth/capabilities'

export type NavGroup = 'Operate' | 'Communicate' | 'Insights' | 'Settings' | 'Platform'

export interface NavItem {
  readonly path: string
  readonly label: string
  readonly icon: LucideIcon
  readonly group: NavGroup
  /** All of these permissions must be held. Empty = visible to every signed-in user. */
  readonly requires: readonly Permission[]
  /** Only when the caller has NO organization (platform / reseller plane). */
  readonly platformOnly?: boolean
}

/**
 * The staff route registry (FRONTEND_PLAN §8). Navigation is DERIVED from this list filtered by
 * the caller's capabilities — no component ever consults a role name. Feature phases append here.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { path: '/', label: 'Home', icon: Home, group: 'Operate', requires: [] },
  {
    path: '/recovery',
    label: 'Recovery',
    icon: Heart,
    group: 'Operate',
    requires: ['recovery-cases:read'],
  },
  {
    path: '/calendar',
    label: 'Calendar',
    icon: CalendarDays,
    group: 'Operate',
    requires: ['appointments:read'],
  },
  {
    path: '/waitlist',
    label: 'Waitlist',
    icon: ListOrdered,
    group: 'Operate',
    requires: ['waitlist:read'],
  },
  {
    path: '/customers',
    label: 'Customers',
    icon: Users,
    group: 'Operate',
    requires: ['customers:read'],
  },
  {
    path: '/inbox',
    label: 'Inbox',
    icon: MessageSquare,
    group: 'Communicate',
    requires: ['conversations:read'],
  },
  {
    path: '/notifications',
    label: 'Notifications',
    icon: Bell,
    group: 'Communicate',
    requires: ['notifications:read'],
  },
  {
    path: '/due-back',
    label: 'Due-back (report)',
    icon: RotateCcw,
    group: 'Insights',
    requires: ['reports:read'],
  },
  {
    path: '/reports',
    label: 'Reports',
    icon: BarChart3,
    group: 'Insights',
    requires: ['reports:read'],
  },
  { path: '/settings', label: 'Settings', icon: Settings, group: 'Settings', requires: [] },
  {
    path: '/platform/resellers',
    label: 'Resellers',
    icon: Handshake,
    group: 'Platform',
    requires: ['resellers:read'],
    platformOnly: true,
  },
  {
    path: '/platform/organizations',
    label: 'Organisations',
    icon: Building2,
    group: 'Platform',
    requires: ['organizations:read'],
    platformOnly: true,
  },
  {
    path: '/platform/billing',
    label: 'Billing overview',
    icon: CreditCard,
    group: 'Platform',
    requires: ['billing:overview'],
    platformOnly: true,
  },
]

export const GROUP_ORDER: readonly NavGroup[] = [
  'Operate',
  'Communicate',
  'Insights',
  'Settings',
  'Platform',
]

export function visibleNavItems(capabilities: Capabilities | null): readonly NavItem[] {
  if (capabilities === null) return []
  const platform = capabilities.organizationId === null
  return NAV_ITEMS.filter(
    (item) =>
      (item.platformOnly === true ? platform : true) &&
      hasAllPermissions(capabilities, item.requires),
  )
}

export function groupedNavItems(
  capabilities: Capabilities | null,
): readonly { readonly group: NavGroup; readonly items: readonly NavItem[] }[] {
  const visible = visibleNavItems(capabilities)
  return GROUP_ORDER.map((group) => ({
    group,
    items: visible.filter((item) => item.group === group),
  })).filter((g) => g.items.length > 0)
}

/** Longest-prefix match so `/settings/profile` highlights the Settings item. */
export function isActivePath(itemPath: string, currentPath: string): boolean {
  if (itemPath === '/') return currentPath === '/'
  return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`)
}
